export interface BookOgImageOptions {
  title: string;
  uibn: string;
  authorName?: string;
  publisherName?: string;
  category?: string;
  publicationYear?: number;
  coverUrl?: string;
}

/**
 * Preloads an image safely for canvas drawing
 */
function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // Fail gracefully
    img.src = url;
  });
}

/**
 * Wraps text into lines that fit inside a max width on Canvas
 */
function wrapCanvasText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number = 3
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0] || '';

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + ' ' + word).width;
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
      if (lines.length === maxLines - 1) {
        break;
      }
    }
  }

  if (currentLine) {
    if (lines.length >= maxLines) {
      currentLine += '...';
    }
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Generates a branded 1200x630 OpenGraph PNG preview image for a book on HTML5 Canvas.
 */
export async function generateBookOgCanvasImage(options: BookOgImageOptions): Promise<string> {
  if (typeof document === 'undefined') return '';

  const width = 1200;
  const height = 630;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // 1. Background Gradient
  const bgGrad = ctx.createLinearGradient(0, 0, width, height);
  bgGrad.addColorStop(0, '#0f172a'); // slate-900
  bgGrad.addColorStop(0.5, '#1e1b4b'); // indigo-950
  bgGrad.addColorStop(1, '#020617'); // slate-950
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // Decorative background circles
  const circleGrad1 = ctx.createRadialGradient(200, 100, 0, 200, 100, 400);
  circleGrad1.addColorStop(0, 'rgba(99, 102, 241, 0.25)'); // indigo-500
  circleGrad1.addColorStop(1, 'rgba(99, 102, 241, 0)');
  ctx.fillStyle = circleGrad1;
  ctx.fillRect(0, 0, width, height);

  const circleGrad2 = ctx.createRadialGradient(1000, 500, 0, 1000, 500, 450);
  circleGrad2.addColorStop(0, 'rgba(168, 85, 247, 0.2)'); // purple-500
  circleGrad2.addColorStop(1, 'rgba(168, 85, 247, 0)');
  ctx.fillStyle = circleGrad2;
  ctx.fillRect(0, 0, width, height);

  // Subtle grid overlay
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
  ctx.lineWidth = 1;
  for (let x = 0; x < width; x += 40) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  for (let y = 0; y < height; y += 40) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  // 2. Top Header Brand Bar
  ctx.save();
  // Brand Pill
  ctx.fillStyle = 'rgba(79, 70, 229, 0.3)';
  ctx.strokeStyle = 'rgba(129, 140, 248, 0.5)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(60, 45, 120, 36, 18);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 18px Inter, sans-serif';
  ctx.fillText('UIBN', 98, 69);

  ctx.fillStyle = '#94a3b8'; // slate-400
  ctx.font = '600 15px Inter, sans-serif';
  ctx.fillText('Universal Indonesian Book Number (UIBN)', 198, 68);
  ctx.restore();

  // 3. Draw Book Cover or Placeholder (Left Side: X=60, Y=115, W=310, H=450)
  const coverX = 60;
  const coverY = 115;
  const coverW = 310;
  const coverH = 450;

  const loadedCoverImg = options.coverUrl ? await loadImage(options.coverUrl) : null;

  ctx.save();
  // Cover shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
  ctx.shadowBlur = 30;
  ctx.shadowOffsetX = 10;
  ctx.shadowOffsetY = 15;

  if (loadedCoverImg) {
    // Draw real loaded book cover image
    ctx.beginPath();
    ctx.roundRect(coverX, coverY, coverW, coverH, 16);
    ctx.clip();
    ctx.drawImage(loadedCoverImg, coverX, coverY, coverW, coverH);
  } else {
    // Render elegant vector placeholder cover
    const coverGrad = ctx.createLinearGradient(coverX, coverY, coverX + coverW, coverY + coverH);
    coverGrad.addColorStop(0, '#312e81');
    coverGrad.addColorStop(1, '#1e1b4b');
    ctx.fillStyle = coverGrad;
    ctx.beginPath();
    ctx.roundRect(coverX, coverY, coverW, coverH, 16);
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Placeholder icon
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = 'bold 24px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('UIBN BOOK', coverX + coverW / 2, coverY + coverH / 2 - 10);
    ctx.font = '14px Inter, sans-serif';
    ctx.fillStyle = '#a5b4fc';
    ctx.fillText('Katalog Resmi', coverX + coverW / 2, coverY + coverH / 2 + 20);
    ctx.textAlign = 'left';
  }
  ctx.restore();

  // 4. Content Area (Right Side: X=410, Y=120, W=730)
  const contentX = 410;
  let currentY = 145;

  // Category Badge
  const categoryText = (options.category || 'Literasi & Umum').toUpperCase();
  ctx.font = 'bold 13px Inter, sans-serif';
  const catWidth = ctx.measureText(categoryText).width + 28;

  ctx.fillStyle = 'rgba(99, 102, 241, 0.2)';
  ctx.strokeStyle = 'rgba(129, 140, 248, 0.4)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(contentX, currentY - 20, catWidth, 30, 15);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#c7d2fe'; // indigo-200
  ctx.fillText(categoryText, contentX + 14, currentY);

  currentY += 45;

  // Book Title
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 36px Inter, sans-serif';
  const titleLines = wrapCanvasText(ctx, options.title, 720, 2);

  for (const line of titleLines) {
    ctx.fillText(line, contentX, currentY);
    currentY += 46;
  }

  currentY += 10;

  // UIBN Code Box
  ctx.fillStyle = 'rgba(15, 23, 42, 0.8)';
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(contentX, currentY, 480, 68, 16);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#818cf8';
  ctx.font = '600 12px Inter, sans-serif';
  ctx.fillText('KODE IDENTIFIKASI UIBN RESMI', contentX + 20, currentY + 25);

  ctx.fillStyle = '#38bdf8'; // sky-400
  ctx.font = 'bold 22px "Courier New", monospace';
  ctx.fillText(options.uibn || 'UIBN-979-0000-0000-0', contentX + 20, currentY + 53);

  currentY += 105;

  // Metadata Grid (Penulis & Penerbit)
  ctx.fillStyle = '#94a3b8';
  ctx.font = '500 14px Inter, sans-serif';
  ctx.fillText('Penulis Karya:', contentX, currentY);
  ctx.fillText('Penerbit Resmi:', contentX + 360, currentY);

  currentY += 24;

  ctx.fillStyle = '#f8fafc';
  ctx.font = 'bold 18px Inter, sans-serif';
  ctx.fillText(options.authorName || 'Penulis Terdaftar', contentX, currentY);
  ctx.fillText(options.publisherName || 'Penerbit Terdaftar', contentX + 360, currentY);

  currentY += 55;

  // Verification Seal / Footer Tag
  ctx.fillStyle = 'rgba(16, 185, 129, 0.15)'; // emerald-500/15
  ctx.strokeStyle = 'rgba(52, 211, 153, 0.4)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(contentX, currentY, 420, 42, 21);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#34d399'; // emerald-400
  ctx.font = 'bold 14px Inter, sans-serif';
  ctx.fillText('✓ Terverifikasi Modulo-10 Checksum UIBN', contentX + 20, currentY + 26);

  // Return generated PNG Data URL
  return canvas.toDataURL('image/png');
}
